<template>
  <v-card
    class="province-list-card"
    :class="cardClasses"
    @click="handleCardClick"
    :disabled="isDisabled"
    hover
    elevation="1"
  >
    <v-card-text class="province-list-content">
      <!-- Pin Button -->
      <div class="province-list-pin">
        <v-tooltip bottom>
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              @click.stop="$emit('toggle-pin', province)"
              :color="isPinned ? '#fddf00' : 'grey-lighten-2'"
              :variant="isPinned ? 'flat' : 'outlined'"
              size="small"
              class="pin-btn-list"
              icon
            >
              <v-icon :color="isPinned ? 'black' : '#999'" size="20">
                {{ isPinned ? "mdi-pin" : "mdi-pin-outline" }}
              </v-icon>
            </v-btn>
          </template>
          <span>{{
            isPinned ? "Unpin from monitoring" : "Pin for monitoring"
          }}</span>
        </v-tooltip>
      </div>

      <!-- Province Image -->
      <div class="province-list-image">
        <div class="list-image" :style="imageStyle"></div>
      </div>

      <!-- Province Info -->
      <div class="province-list-info">
        <h3 class="province-list-name">{{ province.province_name_en }}</h3>
        <p class="province-list-name-th">{{ province.province_name_th }}</p>
      </div>

      <v-spacer></v-spacer>

      <!-- Province Stats -->
      <div class="province-list-stats">
        <div v-if="hasOrders" class="order-count">
          <v-chip color="#fddf00" size="small">
            <span class="order-made-text">ORDER MADE</span>
            <span class="order-count-text">{{ orderAmount }}</span>
          </v-chip>
        </div>

        <div v-if="isUnavailable" class="unavailable-status">
          <v-chip color="error" size="small">
            <v-icon start>mdi-close-circle</v-icon>
            Unavailable
          </v-chip>
        </div>
      </div>

      <!-- Action Button -->
      <div class="province-list-action">
        <v-btn
          v-if="!isUnavailable"
          color="#fddf00"
          variant="elevated"
          size="small"
        >
          <span class="select-btn-text">Select</span>
          <v-icon end color="black">mdi-arrow-right</v-icon>
        </v-btn>
        <v-btn v-else color="grey" variant="outlined" size="small" disabled>
          Unavailable
        </v-btn>
      </div>
    </v-card-text>

    <!-- Loading Overlay -->
    <div v-if="isLoading" class="list-loading-overlay">
      <div class="loading-spinner loading-spinner--small"></div>
      <span class="list-loading-text">Connecting...</span>
    </div>
  </v-card>
</template>

<script>
export default {
  name: "ProvinceListItem",
  props: {
    province: {
      type: Object,
      required: true,
    },
    orderAmount: {
      type: Number,
      default: 0,
    },
    isPinned: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isUnavailable() {
      return this.province.province_onuse === 2;
    },
    isDisabled() {
      return this.isUnavailable || this.isLoading;
    },
    hasOrders() {
      return this.orderAmount > 0;
    },
    cardClasses() {
      return {
        "province-list-card--disabled": this.isUnavailable,
        "province-list-card--loading": this.isLoading,
        "province-list-card--pinned": this.isPinned,
      };
    },
    imageStyle() {
      return {
        backgroundImage: `url(${this.province.province_image})`,
        filter: this.isUnavailable ? "grayscale(100%)" : "",
      };
    },
  },
  methods: {
    handleCardClick() {
      if (!this.isDisabled) {
        this.$emit("select", this.province);
      }
    },
  },
};
</script>

<style scoped>
.province-list-card {
  transition: all 0.2s ease;
  border-radius: 12px;
  position: relative;
}

.province-list-card:hover {
  transform: translateX(4px);
  box-shadow: 0 4px 20px rgba(253, 223, 0, 0.3);
}

.province-list-card--disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.province-list-card--disabled:hover {
  transform: none;
}

.province-list-card--loading {
  pointer-events: none;
  opacity: 0.8;
}

.province-list-card--pinned {
  border-left: 4px solid #fddf00;
  background: linear-gradient(90deg, rgba(253, 223, 0, 0.05), transparent);
}

.province-list-card--pinned::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, #fddf00, #f39c12, transparent);
  z-index: 1;
}

.province-list-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
}

.province-list-pin {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.pin-btn-list {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid rgba(253, 223, 0, 0.3) !important;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.pin-btn-list:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 16px rgba(253, 223, 0, 0.4);
  border-color: rgba(253, 223, 0, 0.6) !important;
}

.province-list-image {
  flex-shrink: 0;
}

.list-image {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  background-size: cover;
  background-position: center;
}

.province-list-info {
  flex: 1;
  min-width: 0;
}

.province-list-name {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: #2c3e50;
}

.province-list-name-th {
  font-size: 0.9rem;
  color: #7f8c8d;
  margin: 0;
}

.province-list-stats {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
}

.order-made-text {
  color: black;
  font-weight: 600;
  font-size: 0.7rem;
  margin-right: 0.25rem;
}

.order-count-text {
  color: black;
  font-weight: 700;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  padding: 0px 4px;
  margin-left: 0.25rem;
}

.province-list-action {
  flex-shrink: 0;
}

.select-btn-text {
  color: black;
  font-weight: 600;
}

.list-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  z-index: 10;
  border-radius: 12px;
}

.loading-spinner--small {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(253, 223, 0, 0.3);
  border-top: 3px solid #fddf00;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.list-loading-text {
  color: #2c3e50;
  font-size: 0.9rem;
  font-weight: 600;
}

/* Enhanced hover states */
.province-list-card:hover .pin-btn-list {
  border-color: rgba(253, 223, 0, 0.6) !important;
  box-shadow: 0 4px 12px rgba(253, 223, 0, 0.25);
}

.province-list-card--pinned:hover {
  background: linear-gradient(90deg, rgba(253, 223, 0, 0.08), transparent);
  transform: translateX(2px);
}

@media (max-width: 768px) {
  .province-list-content {
    gap: 0.75rem;
    padding: 0.75rem;
  }

  .pin-btn-list {
    width: 36px;
    height: 36px;
  }

  .list-image {
    width: 50px;
    height: 50px;
  }

  .province-list-name {
    font-size: 1.1rem;
  }

  .province-list-name-th {
    font-size: 0.85rem;
  }
}

@media (max-width: 480px) {
  .province-list-content {
    gap: 0.5rem;
    padding: 0.5rem;
  }

  .pin-btn-list {
    width: 32px;
    height: 32px;
  }

  .pin-btn-list .v-icon {
    font-size: 16px !important;
  }

  .province-list-stats {
    flex-direction: column;
    gap: 0.25rem;
  }
}
</style>
